import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import Header from "../../Header/Header";
import Footer from "../../Footer/Footer";
import SubVisual from "../Public/SubVisual";
import Lightbox from "./Lightbox";
import { useNotices } from "../../Hooks/useNotices";

const ITEMS_PER_PAGE = 4;

const Notice = () => {
  const { notices, isLoading, isFetching, hasMore, loadMore } =
    useNotices(ITEMS_PER_PAGE);

  const [selectedGallery, setSelectedGallery] = useState(null);

  const handleCardClick = (galleryImages, mainImgUrl) => {
    const fullMainImgUrl = mainImgUrl.startsWith("http")
      ? mainImgUrl
      : `${process.env.REACT_APP_BACK_API_URL}/company/${mainImgUrl}`;

    let parsedGallery = [];
    if (galleryImages) {
      parsedGallery =
        typeof galleryImages === "string"
          ? JSON.parse(galleryImages)
          : galleryImages;
    }

    const fullGalleryUrls = parsedGallery.map((img) =>
      img.startsWith("http")
        ? img
        : `${process.env.REACT_APP_BACK_API_URL}/company/${img}`,
    );

    const combinedImages = Array.from(
      new Set([fullMainImgUrl, ...fullGalleryUrls]),
    );

    setSelectedGallery(combinedImages);
  };

  return (
    <>
      <Header />
      <SubVisual title="게시판" subTitle="고객지원" />

      <PageContainer>
        <InnerWrapper>
          <SectionHeader>
            <h5 className="sub">BOARD</h5>
            <h2 className="main">게시판</h2>
          </SectionHeader>

          <NoticeGrid>
            {isLoading &&
              Array.from({ length: ITEMS_PER_PAGE }).map((_, idx) => (
                <SkeletonCard key={`initial-skel-${idx}`}>
                  <div className="skeleton-img" />
                  <div className="skeleton-text title" />
                  <div className="skeleton-text desc" />
                </SkeletonCard>
              ))}

            {!isLoading &&
              notices.map((notice, idx) => {
                const galleryArray =
                  typeof notice.gallery === "string"
                    ? JSON.parse(notice.gallery)
                    : notice.gallery;

                return (
                  <NoticeCard
                    key={notice.id}
                    delay={(idx % ITEMS_PER_PAGE) * 0.1}
                    onClick={() =>
                      handleCardClick(notice.gallery, notice.imgUrl)
                    }
                  >
                    <ImageWrapper>
                      <img
                        src={`${process.env.REACT_APP_BACK_API_URL}/company/${notice.imgUrl}`}
                        alt={notice.title}
                      />
                      <div className="overlay">
                        <span>
                          {galleryArray && galleryArray.length > 0
                            ? `${galleryArray.length}장의 사진 보기`
                            : "자세히 보기"}
                        </span>
                      </div>
                    </ImageWrapper>
                    <TextContent>
                      <span className="date">{notice.date}</span>
                      <h3 className="title">{notice.title}</h3>
                      <p className="desc">{notice.desc}</p>
                    </TextContent>
                  </NoticeCard>
                );
              })}

            {isFetching &&
              Array.from({ length: ITEMS_PER_PAGE }).map((_, idx) => (
                <SkeletonCard key={`more-skel-${idx}`}>
                  <div className="skeleton-img" />
                  <div className="skeleton-text title" />
                  <div className="skeleton-text desc" />
                </SkeletonCard>
              ))}
          </NoticeGrid>

          {!isLoading && hasMore && (
            <LoadMoreContainer>
              <LoadMoreBtn onClick={loadMore} disabled={isFetching}>
                {isFetching ? "불러오는 중..." : "더보기 (Load More)"}
              </LoadMoreBtn>
            </LoadMoreContainer>
          )}
        </InnerWrapper>
      </PageContainer>

      <Footer />

      {/* 라이트박스 렌더링 */}
      {selectedGallery && (
        <Lightbox
          images={selectedGallery}
          onClose={() => setSelectedGallery(null)}
        />
      )}
    </>
  );
};

export default Notice;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const shimmer = keyframes`
  0% { background-position: -468px 0; }
  100% { background-position: 468px 0; }
`;

const PageContainer = styled.div`
  width: 100%;
  background-color: #fafafa;
  padding: 120px 0 150px 0;
`;

const InnerWrapper = styled.div`
  width: 90%;
  max-width: 1300px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  margin-bottom: 80px;
  animation: ${fadeInUp} 1s ease-out forwards;

  .sub {
    font-size: 0.9rem;
    font-weight: 800;
    /* color: #00d2ff; */
    color: gray;
    letter-spacing: 3px;
    margin-bottom: 15px;
  }
  .main {
    font-size: 3rem;
    font-weight: 800;
    line-height: 1.3;
    color: #1a1a1a;
  }
`;

const NoticeGrid = styled.div`
  display: grid;

  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NoticeCard = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease-out ${(props) => props.delay}s forwards;
  transition: all 0.4s ease;

  &:hover {
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    transform: translateY(-8px);

    img {
      transform: scale(1.08);
    }

    .overlay {
      opacity: 1;
    }

    .title {
      color: #00d2ff;
    }
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 260px;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s cubic-bezier(0.2, 0, 0.2, 1);
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: opacity 0.4s ease;

    span {
      color: #fff;
      font-size: 0.95rem;
      font-weight: 700;
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 10px 24px;
      border-radius: 30px;
      backdrop-filter: blur(4px);
    }
  }
`;

const TextContent = styled.div`
  padding: 30px;

  .date {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #999;
    margin-bottom: 12px;
  }

  .title {
    font-size: 1.35rem;
    font-weight: 800;
    color: #1a1a1a;
    line-height: 1.4;
    margin-bottom: 15px;
    transition: color 0.3s ease;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .desc {
    font-size: 1rem;
    color: #666;
    line-height: 1.6;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const SkeletonCard = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  height: 420px;

  .skeleton-img {
    width: 100%;
    height: 260px;
    background: #f6f7f8;
    background-image: linear-gradient(
      to right,
      #f6f7f8 0%,
      #edeef1 20%,
      #f6f7f8 40%,
      #f6f7f8 100%
    );
    background-repeat: no-repeat;
    background-size: 800px 104px;
    animation: ${shimmer} 1.5s infinite linear forwards;
  }

  .skeleton-text {
    margin: 30px;
    background: #f6f7f8;
    background-image: linear-gradient(
      to right,
      #f6f7f8 0%,
      #edeef1 20%,
      #f6f7f8 40%,
      #f6f7f8 100%
    );
    background-repeat: no-repeat;
    background-size: 800px 100px;
    animation: ${shimmer} 1.5s infinite linear forwards;
    border-radius: 4px;

    &.title {
      height: 24px;
      width: 80%;
      margin-bottom: 20px;
    }
    &.desc {
      height: 40px;
      width: 100%;
    }
  }
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 80px;
  animation: ${fadeInUp} 1s ease-out forwards;
`;

const LoadMoreBtn = styled.button`
  background: transparent;
  color: #1a1a1a;
  font-size: 1rem;
  font-weight: 700;
  padding: 16px 50px;
  border: 2px solid #1a1a1a;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    background: #1a1a1a;
    color: #fff;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #ccc;
    color: #888;
  }
`;
